const VOID_ELEMENTS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr',
]);

const RAW_TEXT_ELEMENTS = new Set(['script', 'style']);

const PLACEHOLDER_PREFIX = '\u200B\u200BCF_';
const PLACEHOLDER_SUFFIX = '\u200B\u200B';

function fixOrphanClosingTags(html) {
  let result = '';
  let i = 0;
  const stack = [];
  const len = html.length;
  const lowerHtml = html.toLowerCase();

  function skipToEndOfTag(start) {
    let pos = start;
    if (pos >= len || html[pos] !== '<') return -1;
    pos += 1;
    if (html[pos] === '/') return -1;
    while (pos < len && /[a-zA-Z0-9:-]/.test(html[pos])) pos += 1;
    const tagName = html.slice(start + 1, pos).toLowerCase();
    if (!tagName) return -1;
    while (pos < len) {
      const c = html[pos];
      if (c === '"' || c === "'") {
        const q = c;
        pos += 1;
        while (pos < len && html[pos] !== q) {
          if (html[pos] === '\\') pos += 1;
          pos += 1;
        }
        if (pos < len) pos += 1;
      } else if (c === '>') {
        return { end: pos, tagName };
      } else {
        pos += 1;
      }
    }
    return -1;
  }

  while (i < len) {
    const openAngle = html.indexOf('<', i);
    if (openAngle === -1) {
      result += html.slice(i);
      break;
    }
    result += html.slice(i, openAngle);
    i = openAngle;

    if (html.slice(i, i + 2) === '</') {
      const closeAngle = html.indexOf('>', i);
      if (closeAngle === -1) {
        result += html.slice(i);
        break;
      }
      const tagName = html.slice(i + 2, closeAngle).trim().toLowerCase();
      if (!/^[a-z][a-z0-9:-]*$/.test(tagName)) {
        result += html[i];
        i += 1;
        continue;
      }
      const stackIndex = stack.lastIndexOf(tagName);
      if (stackIndex !== -1) {
        while (stack.length > stackIndex) {
          result += `</${stack.pop()}>`;
        }
      } else {
        result += `<${tagName}></${tagName}>`;
      }
      i = closeAngle + 1;
      continue;
    }

    const parsed = skipToEndOfTag(i);
    if (parsed === -1) {
      result += html[i];
      i += 1;
      continue;
    }
    const { end, tagName } = parsed;
    result += html.slice(i, end + 1);
    i = end + 1;
    if (VOID_ELEMENTS.has(tagName)) continue;
    stack.push(tagName);
    if (RAW_TEXT_ELEMENTS.has(tagName)) {
      const close = lowerHtml.indexOf(`</${tagName}`, i);
      const contentEnd = close === -1 ? len : close;
      result += html.slice(i, contentEnd);
      i = contentEnd;
    }
  }

  return result;
}

function extractUnclosedScriptStyle(html) {
  const replacements = [];
  let work = html;

  function findUnclosed(openTag, closeTag) {
    const lastOpen = work.lastIndexOf(openTag);
    if (lastOpen === -1) return null;
    const afterOpen = work.indexOf('>', lastOpen);
    if (afterOpen === -1) return null;
    const contentStart = afterOpen + 1;
    const nextClose = work.indexOf(closeTag, contentStart);
    if (nextClose !== -1) return null;
    return {
      start: lastOpen,
      contentStart: afterOpen + 1,
      end: work.length,
      content: work.slice(contentStart),
      openTagFull: work.slice(lastOpen, contentStart),
      closeTag,
    };
  }

  let done = false;
  while (!done) {
    done = true;
    const unclosedScript = findUnclosed('<script', '</script>');
    const unclosedStyle = findUnclosed('<style', '</style>');
    const pick = unclosedScript && (!unclosedStyle || unclosedScript.start >= unclosedStyle.start)
      ? { ...unclosedScript, type: 'script' }
      : unclosedStyle
        ? { ...unclosedStyle, type: 'style' }
        : null;
    if (pick) {
      const id = replacements.length;
      const placeholder = `${PLACEHOLDER_PREFIX}${pick.type.toUpperCase()}_${id}${PLACEHOLDER_SUFFIX}`;
      const newSegment = pick.openTagFull + placeholder + pick.closeTag;
      work = work.slice(0, pick.start) + newSegment;
      replacements.push({ placeholder, content: pick.content, type: pick.type });
      done = false;
    }
  }

  return { html: work, replacements };
}

function restoreScriptStyleContent(html, replacements) {
  let result = html;
  for (const { placeholder, content, type } of replacements) {
    let restoredContent = content;
    if (type === 'script') {
      restoredContent = content.replace(/"/g, "'");
    }
    result = result.split(placeholder).join(restoredContent);
  }
  return result;
}

const fixUnclosedHtmlTags = (htmlString) => {
  try {
    if (typeof htmlString !== 'string') {
      throw new Error("Invalid input: HTML string is required");
    }

    let work = fixOrphanClosingTags(htmlString);
    const { html: preprocessed, replacements } = extractUnclosedScriptStyle(work);

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<cf-fix>${preprocessed}</cf-fix>`, 'text/html');
    const wrapper = doc.querySelector('cf-fix');

    wrapper.querySelectorAll('table tbody').forEach((tbody) => {
      while (tbody.firstChild) {
        tbody.parentNode.insertBefore(tbody.firstChild, tbody);
      }
      tbody.remove();
    });

    let fixedHtml = wrapper.innerHTML.trim();
    fixedHtml = restoreScriptStyleContent(fixedHtml, replacements);

    if (htmlString !== fixedHtml) {
      console.warn(`CF: Fixed missing tags detected.`, {
        original: htmlString,
        fixed: fixedHtml,
      });
    }

    return fixedHtml;
  } catch (error) {
    console.error("Error processing HTML:", error);
    return "";
  }
};

export default fixUnclosedHtmlTags;
